import { SignInRequired } from "../../auth/signIn";
import { formatVolume, formatAlcohol } from "./eventStats/utils";
import React from "react";
import {Alert, Col, DatePicker, Divider, Row, Space, theme} from "antd";
import { Link } from "react-router-dom";
import { ErrorState, PageHeader, StatTile } from "@dowty/ui";
import dayjs from "dayjs";
import useDrinkStats from "./useDrinkStats";
import PromilleTimelineCard from "./drinkStats/PromilleTimelineCard";
import RecentDrinksCard from "./drinkStats/RecentDrinksCard";
import SummaryStatsGrid from "./drinkStats/SummaryStatsGrid";
import EditActivityModal from "./drinkStats/EditActivityModal";
import UserMetabolismCard from "./drinkStats/UserMetabolismCard";
import "./AnalyticsPages.css";
import { useOnlineStatus } from "@dowty/offline";

const DrinkStats: React.FC = () => {
    const { token } = theme.useToken();
    const isOnline = useOnlineStatus();
    const {
        isAuthenticated, user, t, loading, activitiesError, selectedDay, setSelectedDay, setHasUserSelectedDay,
        weightKg, setWeightKg, sex, setSex, loadingDrinks, isEditModalVisible,
        isSavingEdit, editDrinkId, setEditDrinkId, editAmount, setEditAmount, editTimestamp,
        setEditTimestamp, editDurationMinutes, setEditDurationMinutes, selectedDayDisplay,
        isSelectedDayToday, latestPromille, latestPromilleRaw, latestBreathMgL, selectedDayPeakPromille, selectedDaySoberInfo,
        selectedDayTimeline, hasSelectedDayData,
        selectedDayActivitiesDesc, loadActivities, handleOpenEditModal, handleDeleteActivity,
        volumeStats, alcoholStats, promilleStats, drinkSelectOptions, handleEditModalClose,
        handleEditSubmit, editStartTimestamp, handleEditStartChange,
    } = useDrinkStats();
    if (!isAuthenticated || !user) {
        return <SignInRequired title={t("drinkStats.authRequired.title")} description={t("drinkStats.authRequired.description")} />;
    }

    return (
        <div className="analytics-page drink-stats-page">
            <PageHeader
                title={t("drinkStats.page.title")}
                subtitle={t("drinkStats.page.subtitle", { date: selectedDayDisplay.format("DD MMM YYYY") })}
                extra={
                    <Space wrap>
                        <DatePicker
                            className="analytics-date-picker"
                            value={selectedDay}
                            allowClear={false}
                            onChange={(value) => {
                                setHasUserSelectedDay(true);
                                setSelectedDay(value ? value.startOf("day") : dayjs().startOf("day"));
                            }}
                            disabledDate={(current) => Boolean(current?.isAfter(dayjs(), "day"))}
                            format="DD MMM YYYY"
                        />
                        <Link to="/drinks/recap" className="ant-btn ant-btn-default recap-entry-link">{t("recap.page.title")}</Link>
                    </Space>
                }
            />
            {activitiesError ? <ErrorState message={activitiesError} retry={() => { void loadActivities(); }} retryLabel={t("recap.retry")} /> : <>
            {!isOnline && !loading && selectedDayActivitiesDesc.some((activity) => activity.activityId) && <Alert type="info" showIcon message={t("drinkStats.recentDrinks.deleteOffline")} />}

            <Row gutter={[12, 12]} className="drink-stats-summary" aria-label={t("drinkStats.page.title")}>
                {[volumeStats[0], alcoholStats[0]].map((item) => (
                    <Col xs={24} sm={12} key={item.key}>
                        <StatTile label={item.title} value={item.suffix === "ml" ? formatVolume(item.value) : formatAlcohol(item.value)} />
                    </Col>
                ))}
            </Row>

            <UserMetabolismCard
                weightKg={weightKg}
                sex={sex}
                onWeightChange={(value) => setWeightKg(value)}
                onSexChange={(value) => setSex(value)}
                isSelectedDayToday={isSelectedDayToday}
                latestPromille={latestPromille}
                rawPromille={latestPromilleRaw}
                peakPromille={selectedDayPeakPromille}
                breathMgL={latestBreathMgL}
                soberInfo={selectedDaySoberInfo}
            />

            <PromilleTimelineCard
                timeline={selectedDayTimeline}
                selectedDay={selectedDayDisplay}
                hasSelectedDayData={hasSelectedDayData}
                soberInfo={selectedDaySoberInfo}
                token={token}
            />

            <Divider />

            <RecentDrinksCard
                activities={selectedDayActivitiesDesc}
                selectedDay={selectedDayDisplay}
                isOnline={isOnline}
                loading={loading}
                hasSelectedDayData={hasSelectedDayData}
                onRefresh={loadActivities}
                onEdit={(activity) => {
                    void handleOpenEditModal(activity);
                }}
                onDelete={handleDeleteActivity}
            />

            <Divider />

            <SummaryStatsGrid items={volumeStats} />
            <SummaryStatsGrid items={alcoholStats} />
            <SummaryStatsGrid items={promilleStats} />
            </>}

            <EditActivityModal
                open={isEditModalVisible}
                isSaving={isSavingEdit}
                token={token}
                drinkOptions={drinkSelectOptions}
                loadingDrinks={loadingDrinks}
                selectedDrinkId={editDrinkId}
                onClose={handleEditModalClose}
                onSubmit={handleEditSubmit}
                onDrinkChange={(value) => setEditDrinkId(value)}
                amount={editAmount}
                onAmountChange={(value) => setEditAmount(value)}
                timestamp={editTimestamp}
                onTimestampChange={(value) => setEditTimestamp(value)}
                durationMinutes={editDurationMinutes}
                onDurationChange={(value) => setEditDurationMinutes(Math.max(0, value ?? 0))}
                startTimestamp={editStartTimestamp}
                onStartChange={handleEditStartChange}
            />
        </div>
    );
};

export default DrinkStats;
